import { FirebaseError } from 'firebase/app';
import {
  collectionGroup,
  getDocs,
  query,
  where,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerWithCrop {
  customerId: string;
  cropTypeId: string;
  regionId: string;
}

export interface CropStatusServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toServiceError(error: unknown): CropStatusServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Service functions ────────────────────────────────────────────────────────

/**
 * Returns deduplicated customer IDs that have at least one non-deleted crop
 * in the given region and org, using a collection-group query across all crops subcollections.
 * Crop docs store `orgId` to enable org-scoped collectionGroup queries.
 */
export async function getCustomerIdsByRegion(orgId: string, regionId: string): Promise<string[]> {
  try {
    const q = query(
      collectionGroup(db, 'crops'),
      where('orgId', '==', orgId),
      where('regionId', '==', regionId),
      where('isDeleted', '==', false),
    );
    const snapshot = await getDocs(q);
    const ids = snapshot.docs.map(d => d.ref.parent.parent?.id).filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Queries all active CustomerCrop documents in an org using a collection-group query.
 * Both filters are optional — supply either, both, or neither to narrow the result set.
 * Crop docs store `orgId` to enable org-scoped collectionGroup queries.
 *
 * Returns an array of { customerId, cropTypeId, regionId } derived from each
 * matching document's reference path and field data.
 */
export async function getCustomerCropsForStatus(orgId: string, opts: {
  cropTypeId?: string;
  regionId?: string;
}): Promise<CustomerWithCrop[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('orgId', '==', orgId),
      where('isDeleted', '==', false),
    ];
    if (opts.cropTypeId) constraints.push(where('cropTypeId', '==', opts.cropTypeId));
    if (opts.regionId)   constraints.push(where('regionId',   '==', opts.regionId));

    const q = query(collectionGroup(db, 'crops'), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(d => ({
      customerId: d.ref.parent.parent!.id,
      cropTypeId: (d.data() as DocumentData).cropTypeId as string,
      regionId:   (d.data() as DocumentData).regionId   as string,
    }));
  } catch (error) {
    throw toServiceError(error);
  }
}
